import type { TBookingContext } from '@providers/booking/types'
import { getDateRanges } from 'hooks/use_booking_reservation'
import type {
  BookingConfigsClientDto,
  BookingSearchConfigsClientDto,
  BookingReservationClientDto,
  InvoiceClientDto,
  OrderClientDto,
  PaymentSettingClientDto,
} from 'service-api/src/types/api'
import type { PaymentSettingAdminDto } from 'service-api/src/types'
import type { Types } from 'service-api'

const pickRestrictionHours = (
  searchFirst?: Types.RestrictionClientDto[] | null,
  fallback?: Types.RestrictionClientDto[]
): Types.RestrictionClientDto[] => {
  if (searchFirst != null && searchFirst.length > 0) {
    return searchFirst
  }
  return fallback ?? []
}

export const mergeBookingConfigPreferSearchHours = (
  bookingConfigs: BookingConfigsClientDto | null | undefined,
  bookingSearch: BookingSearchConfigsClientDto | undefined
): BookingConfigsClientDto | undefined => {
  if (!bookingConfigs) {
    return undefined
  }
  return {
    ...bookingConfigs,
    checkInHour: pickRestrictionHours(bookingSearch?.checkInHour, bookingConfigs.checkInHour),
    checkOutHour: pickRestrictionHours(bookingSearch?.checkOutHour, bookingConfigs.checkOutHour),
    operationHour: pickRestrictionHours(bookingSearch?.operationHour, bookingConfigs.operationHour),
  }
}

const primaryOfferDisplayName = (state: TBookingContext): string | undefined => {
  const g = state.cart.groups?.[0]
  return g?.offerSelected?.offer?.displayName ?? undefined
}

export const buildSyntheticOrderForSurchargePolicy = (
  state: TBookingContext,
  surchargePaymentResults: PaymentSettingClientDto[] | undefined
): OrderClientDto | undefined => {
  const pst = state.data.serviceType
  const primaryId = pst?.id
  if (!primaryId) {
    return undefined
  }

  const ranges = getDateRanges(state)
  if (!ranges.length) {
    return undefined
  }

  const displayName = primaryOfferDisplayName(state)
  const lstItem = {
    locationServiceTypeId: primaryId,
    locationServiceType: { id: primaryId },
    displayName: displayName ?? undefined,
  }

  const overnight = pst?.serviceType?.overnight === true
  const isDaycareLayout = state.serviceTypeName === 'daycare' || !overnight

  let invoices: InvoiceClientDto[]

  if (isDaycareLayout && ranges.length > 0) {
    invoices = ranges.map((r) => ({
      isInStay: false,
      period: [
        {
          startDate: r.startDate,
          endDate: r.endDate,
          startTime: null,
          endTime: null,
        },
      ],
      items: [lstItem],
    }))
  } else {
    const r = ranges[0]!
    invoices = [
      {
        isInStay: false,
        period: [
          {
            startDate: r.startDate,
            endDate: r.endDate,
            startTime: null,
            endTime: null,
          },
        ],
        items: [lstItem],
      },
    ]
  }

  return {
    primaryServiceType: {
      id: primaryId,
      serviceType: {
        overnight: pst?.serviceType?.overnight,
      },
    },
    invoices,
    data: {
      surchargeSettings: (surchargePaymentResults ?? []) as unknown as PaymentSettingAdminDto[],
    },
  } as unknown as OrderClientDto
}

export const buildArrivalStepReservationView = (
  state: TBookingContext,
  bookingConfigs: BookingConfigsClientDto | undefined,
  bookingSearch: BookingSearchConfigsClientDto | undefined,
  surchargePaymentResults: PaymentSettingClientDto[] | undefined
): BookingReservationClientDto | undefined => {
  if (!bookingConfigs?.locationBookingConf) {
    return undefined
  }
  const order = buildSyntheticOrderForSurchargePolicy(state, surchargePaymentResults)
  if (!order) {
    return undefined
  }

  return {
    order,
    locationBookingConf: bookingConfigs.locationBookingConf,
    flexBookingConfs: bookingConfigs.flexBookingConfs,
    checkInHour: pickRestrictionHours(bookingSearch?.checkInHour, bookingConfigs.checkInHour),
    checkOutHour: pickRestrictionHours(bookingSearch?.checkOutHour, bookingConfigs.checkOutHour),
    operationHour: pickRestrictionHours(bookingSearch?.operationHour, bookingConfigs.operationHour),
  } as BookingReservationClientDto
}
